import { Component, Input, OnInit, forwardRef, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableModule } from '@angular/material/table';
import { Subject, takeUntil } from 'rxjs';
import { InformationsRenouvellementFormValue, CdisationData } from '@models/api/requests';

@Component({
    selector: 'app-informations-renouvellement-form',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatDividerModule,
        MatRadioModule,
        MatTableModule
    ],
    templateUrl: './informations-renouvellement-form.component.html',
    styleUrls: ['./informations-renouvellement-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InformationsRenouvellementFormComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InformationsRenouvellementFormComponent),
            multi: true
        }
    ]
})
export class InformationsRenouvellementFormComponent implements OnInit, ControlValueAccessor, OnDestroy, Validator {
  @Input() readOnly = false;
  @Input() cdisationData: CdisationData = {
    dateFinContrat: '',
    nbJoursAvantCdisation: 0,
    nbJoursAbsences: 0,
    nbJoursAbsencesSNF: 0,
    absenceMO: 0,
    absenceATMP: 0,
    absenceMDML: 0,
    absenceMAPA: 0,
    totalJoursAbsences: 0,
    flagCdisation: false,
    absenceDateDebut: '',
    absenceDateFin: '',
    snfDateDebut: '',
    snfDateFin: ''
  };
  @Output() PJObligatoire = new EventEmitter<any>();

  form: FormGroup;
  showRefusConfirmation = false;

  private readonly destroy$ = new Subject<void>();
  private onChange: (value: InformationsRenouvellementFormValue) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      motif: [null, Validators.required],
      confirmRefus: [false]
    });
  }

  ngOnInit(): void {
    // Subscribe to form value changes to notify parent
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onChange(value);
      this.onTouched();
    });

    // Listen to motif changes
    this.form.get('motif')?.valueChanges.subscribe((value) => {
      this.handleMotifChange(value);
    });
  }

  private handleMotifChange(motif: string, resetValue = true) {
    const confirmRefusControl = this.form.get('confirmRefus');

    if (motif === 'refusContratDemande') {
      // Update the existing control with the required validator
      confirmRefusControl?.setValidators(Validators.requiredTrue);
      if (resetValue) {
        confirmRefusControl?.setValue(false);
      }
      confirmRefusControl?.updateValueAndValidity();
      this.showRefusConfirmation = true;
      this.PJObligatoire.emit(false);
    } else {
      // Remove validators when not in refus mode
      confirmRefusControl?.clearValidators();
      confirmRefusControl?.updateValueAndValidity();
      this.showRefusConfirmation = false;

      if (motif === 'refusContratConditions') {
        this.PJObligatoire.emit(true);
      } else {
        this.PJObligatoire.emit(false);
      }
    }
  }

  isMotifDisabled(motif: string): boolean {
    if (this.cdisationData.flagCdisation) {
      // motifs that are never disabled
      return motif !== 'contratConditionsDiffs' && motif !== 'refusContratDemande' && motif !== 'refusContratConditions';
    }
    return false;
  }

  // ControlValueAccessor methods
  writeValue(value: InformationsRenouvellementFormValue): void {
    if (value) {
      this.form.patchValue(value, { emitEvent: false });
      // Trigger motif change handler to update UI state
      if (value.motif) {
        this.handleMotifChange(value.motif, false);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : { invalidInformationsRenouvellementData: true };
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable({ emitEvent: false }) : this.form.enable({ emitEvent: false });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
